import { useNavigate, Link } from "react-router-dom";
import { Shield, ArrowRight, ShoppingCart, Eye, BarChart3, Lock, Bell, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { PRICING } from "@/lib/pricing";
import { LegalFooter } from "@/components/LegalLinks";

const features = [
  { icon: ShoppingCart, title: "Automatic Tracking", desc: "Detects your orders the moment you check out — right in your browser, no inbox scanning required. Works on Amazon, eBay, and Walmart. Premium adds Gmail backfill for purchases made elsewhere." },
  { icon: Eye, title: "Monthly Clarity", desc: "Set a monthly intention for spending and watch your progress in real time — no spreadsheets, no guesswork." },
  { icon: BarChart3, title: "Pattern Awareness", desc: "Spot the habits behind the purchases. Monthly history to help you understand your own spending patterns." },
  { icon: Lock, title: "Clarity Lock", desc: "Commit to your budget when you're feeling clear-headed. Locks edits so impulse moments don't undo your intentions." },
  { icon: Bell, title: "Gentle Nudges", desc: "Soft reminders as you approach your limit. Awareness, not alarm. You stay in control." },
  { icon: Shield, title: "Private by Design", desc: "We never store email content. Just the amount, date, and store — nothing more." },
];

const pricingPlans = [
  {
    name: "Basic",
    price: "$0",
    period: "/forever",
    trialNote: "No credit card, no trial needed",
    features: [
      "Automatic Amazon order tracking",
      "Monthly budget & spending dashboard",
      "Gentle budget nudges (75%, 90%, 100%)",
      "Full spending history",
      "Awareness streak",
      "Chrome extension nudges on Amazon",
    ],
    cta: "Get Started Free",
    highlighted: false,
  },
  {
    name: "Premium",
    price: PRICING.price,
    period: PRICING.perInterval,
    trialNote: "Start with a 30-day free trial",
    features: [
      "Everything in Basic, plus:",
      "All retailers — Amazon, eBay, Walmart",
      "Import purchases from Gmail (phone, other browsers, past orders)",
      "Clarity Lock (lock budget edits)",
      "Adjustable pause timer",
      "CSV export",
    ],
    cta: "Start Free Trial",
    highlighted: true,
  },
];

export default function Landing() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-background" data-testid="landing-page">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-md border-b border-border" data-testid="landing-nav">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center">
            <img src="/logo-lockup.svg" alt="CartControl" className="h-8 w-auto" />
          </Link>
          <div className="hidden md:flex items-center gap-8">
            <a href="#features" className="text-sm text-gray-500 hover:text-gray-900 transition-colors">Features</a>
            <a href="#pricing" className="text-sm text-gray-500 hover:text-gray-900 transition-colors">Pricing</a>
          </div>
          <div className="flex items-center gap-3">
            <Button variant="ghost" className="text-sm text-gray-600 hover:text-gray-900" onClick={() => navigate("/auth")} data-testid="nav-login-btn">
              Log in
            </Button>
            <Button className="h-10 px-6 bg-navy hover:bg-navy-hover active:bg-navy-active text-white text-sm font-medium rounded-lg" onClick={() => navigate("/auth?mode=register")} data-testid="nav-signup-btn">
              Sign up
            </Button>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="pt-32 pb-20 md:pt-40 md:pb-32 px-6" data-testid="hero-section">
        <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-12 gap-12 items-center">
          <div className="md:col-span-7 animate-fade-in-up">
            <Badge variant="outline" className="mb-6 px-3 py-1 text-xs font-medium text-gray-600 border-border">
              A mindful spending companion
            </Badge>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-navy tracking-tight leading-[1.1] font-display">
              Make space between<br />
              <span className="text-sage">impulse and checkout.</span>
            </h1>
            <p className="mt-6 text-base md:text-lg text-gray-500 max-w-lg leading-relaxed">
              CartControl helps you build awareness around your spending. Automatic receipt tracking, gentle budget nudges, and pattern insights — so you can make purchases you feel good about.
            </p>
            <div className="mt-10 flex flex-col sm:flex-row gap-4">
              <Button className="h-12 px-8 bg-navy hover:bg-navy-hover active:bg-navy-active text-white font-medium rounded-lg text-sm" onClick={() => navigate("/auth?mode=register")} data-testid="hero-cta-btn">
                Get Started Free <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
              <Button variant="outline" className="h-12 px-8 border-border text-gray-700 hover:bg-muted font-medium rounded-lg text-sm" onClick={() => document.getElementById("pricing")?.scrollIntoView({ behavior: "smooth" })} data-testid="hero-pricing-btn">
                View Pricing
              </Button>
            </div>
            <p className="mt-4 text-xs text-gray-400">30-day free trial. No credit card required.</p>
          </div>

          {/* Demo Card */}
          <div className="md:col-span-5 animate-fade-in-up stagger-2">
            <div className="bg-white rounded-xl border border-border shadow-sm p-8">
              <div className="flex items-center justify-between mb-6">
                <span className="text-xs font-medium uppercase tracking-wider text-gray-500">May 2026</span>
                <Badge className="bg-sage/10 text-sage border-sage/30 border text-xs font-medium">On Track</Badge>
              </div>
              <div className="mb-2">
                <span className="text-3xl font-bold text-navy font-display">$347.82</span>
                <span className="text-sm text-gray-400 ml-2">/ $500.00</span>
              </div>
              <div className="w-full bg-gray-100 rounded-full h-2 mb-6">
                <div className="bg-sage h-2 rounded-full transition-all duration-700" style={{ width: "69.5%" }}></div>
              </div>
              <div className="space-y-3 mb-5">
                {[
                  { name: "Echo Dot Smart Speaker", amount: "$49.99", date: "May 12" },
                  { name: "USB-C Charging Cable", amount: "$12.99", date: "May 10" },
                  { name: "Wireless Mouse", amount: "$29.99", date: "May 8" },
                ].map((item, i) => (
                  <div key={i} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
                    <div>
                      <p className="text-sm font-medium text-gray-700">{item.name}</p>
                      <p className="text-xs text-gray-400">{item.date}</p>
                    </div>
                    <span className="text-sm font-semibold text-gray-900">{item.amount}</span>
                  </div>
                ))}
              </div>
              <div className="p-3 rounded-lg bg-reflection border border-reflection-border">
                <p className="text-xs text-reflection-foreground font-medium">Still feels worth it? You're at 70% of your budget.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="py-20 md:py-32 px-6 bg-white" data-testid="features-section">
        <div className="max-w-7xl mx-auto">
          <div className="max-w-2xl">
            <span className="text-xs font-medium uppercase tracking-wider text-gray-500">Features</span>
            <h2 className="mt-3 text-3xl font-semibold text-navy tracking-tight font-display">
              Designed to help you pause and reflect
            </h2>
            <p className="mt-4 text-base text-gray-500 leading-relaxed">
              Gentle tools that create space between impulse and purchase.
            </p>
          </div>
          <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((f, i) => (
              <div key={i} className="group p-6 rounded-xl border border-border bg-white hover:shadow-md transition-shadow duration-200" data-testid={`feature-card-${i}`}>
                <div className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center mb-4 group-hover:bg-navy transition-colors duration-200">
                  <f.icon className="w-5 h-5 text-gray-600 group-hover:text-white transition-colors duration-200" />
                </div>
                <h3 className="text-base font-semibold text-gray-900 font-display">{f.title}</h3>
                <p className="mt-2 text-sm text-gray-500 leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="py-20 md:py-32 px-6 bg-background" data-testid="pricing-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center max-w-2xl mx-auto">
            <span className="text-xs font-medium uppercase tracking-wider text-gray-500">Pricing</span>
            <h2 className="mt-3 text-3xl font-semibold text-navy tracking-tight font-display">
              Simple, honest pricing
            </h2>
            <p className="mt-4 text-base text-gray-500">30-day free trial. Full access. No credit card required.</p>
          </div>
          <div className="mt-16 grid sm:grid-cols-2 gap-8 max-w-3xl mx-auto items-start">
            {pricingPlans.map((plan, i) => (
              <div
                key={i}
                className={`rounded-xl bg-white p-8 ${plan.highlighted ? "border-2 border-navy shadow-md" : "border border-border shadow-sm"}`}
                data-testid={`pricing-card-${plan.name.toLowerCase().replace(/\s/g, '-')}`}
              >
                {plan.highlighted ? (
                  <Badge className="mb-4 bg-navy text-white text-xs">30-Day Free Trial</Badge>
                ) : (
                  <Badge variant="outline" className="mb-4 border-border text-gray-500 text-xs">Free Forever</Badge>
                )}
                <h3 className="text-xl font-semibold text-navy font-display">{plan.name}</h3>
                <div className="mt-4 flex items-baseline gap-1">
                  <span className="text-4xl font-bold text-navy font-display">{plan.price}</span>
                  <span className="text-sm text-gray-500">{plan.period}</span>
                </div>
                <p className="mt-1 text-sm text-gray-400">{plan.trialNote}</p>
                <ul className="mt-8 space-y-3">
                  {plan.features.map((f, j) => (
                    <li key={j} className="flex items-start gap-2.5 text-sm text-gray-600">
                      <CheckCircle2 className={`w-4 h-4 mt-0.5 shrink-0 ${plan.highlighted ? "text-sage" : "text-gray-400"}`} />
                      {f}
                    </li>
                  ))}
                </ul>
                <Button
                  variant={plan.highlighted ? "default" : "outline"}
                  className={`mt-8 w-full h-11 font-medium rounded-lg text-sm ${plan.highlighted ? "bg-navy hover:bg-navy-hover active:bg-navy-active text-white" : "border-border text-navy hover:bg-muted"}`}
                  onClick={() => navigate("/auth")}
                  data-testid={`pricing-cta-${plan.name.toLowerCase()}`}
                >
                  {plan.cta}
                </Button>
                <p className="mt-3 text-center text-xs text-gray-400">
                  {plan.highlighted ? `Then ${PRICING.priceWithInterval}. Cancel anytime.` : "No card required. Upgrade anytime."}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 border-t border-border bg-white" data-testid="landing-footer">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <img src="/logo-lockup.svg" alt="CartControl" className="h-6 w-auto opacity-50" />
          <div className="flex flex-col items-center md:items-end gap-1.5">
            <p className="text-xs text-gray-400">Private by design. We never store your email content.</p>
            <LegalFooter />
          </div>
        </div>
      </footer>
    </div>
  );
}
